/** Launch-state card for the Review & run board — the go-live switch made visible.
 *
 *  The launch policy is THE manual go-live switch: a wizard tenant starts on
 *  "Draft", whose `incoming_webhooks=false` drops bookings on BOTH the webhook
 *  and the backfill path (`build_xml` is shared), so the Booking backfill step
 *  cannot start until tours move to Soft launched. The card shows the three
 *  switches together — policy, restech send flags, notification enabled state —
 *  so they cannot silently disagree, and surfaces the trap: the batch op sets
 *  the policy PER TOUR while the tenant default stays Draft, so tours a later
 *  catalogue sync creates inherit Draft again. Hence the live "N tours on Draft"
 *  count and the tenant-default setter (the TO PATCH of default_launch_policy,
 *  the setter that already exists — no new mutation).
 */
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import type { LaunchState, LaunchStatePolicy } from '@/api/preflight';
import * as tourOperatorsApi from '@/api/tourOperators';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import * as easyApi from '@/api/easyOnboarding';

import { easyGoLiveRequired, easyGoLiveRequiredWords } from '../../onboard/easyGoLiveRequired';
import { easyOnboardHref, useEasyGoLiveGate } from '../../onboard/useEasyGoLiveGate';
import { LaunchPolicyPicker, policyAdmits } from './LaunchPolicyPicker';

export { policyAdmits };

/** Deep link to the Tours grid showing this tenant's tours on one policy.
 *
 *  The grid has no URL parameter that opens a batch dialog (the only intent
 *  param is `?new` for create forms), so the closest deep link pre-filters the
 *  grid — `?tenant=` plus the QuerySearch `launch_policy=#<id>` match-by-id —
 *  and the operator selects all → "Set launch policy". */
export function toursOnPolicyLink(topId: number, policyId: number): string {
  const sp = new URLSearchParams({ tenant: String(topId), q: `launch_policy=#${policyId}` });
  return `/tours?${sp.toString()}`;
}

function onOff(v: boolean | null): string {
  return v == null ? 'unset' : v ? 'on' : 'off';
}

interface Props {
  topId: number;
  state: LaunchState;
  /** console_preflight_manage — gates nothing here by itself; the tenant-default
   *  setter additionally needs console_top_manage (it is the TO PATCH). */
  canManage: boolean;
}

export function LaunchStateCard({ topId, state, canManage }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canSetDefault = canManage && hasPerm('console_top_manage');
  // An easy tenant still under the outbound guard goes live from its easy setup; this setter
  // switches the policy only (see useEasyGoLiveGate).
  const easy = useEasyGoLiveGate(topId, canSetDefault);
  // "Move these tours" opens the Tours grid's Set launch policy — policy-only too. Whoever can
  // ask the easy state waits for its answer; whoever cannot keeps the link (the grid's batch
  // operation is checked by the backend either way).
  const canMoveTours = !canSetDefault || easy.open;

  const def = state.default_launch_policy;
  const defaultRow: LaunchStatePolicy | undefined = def
    ? state.policies.find((p) => p.id === def.id)
    : undefined;
  const blocked = !state.incoming_bookings_admitted;
  const [pick, setPick] = useState<number | ''>('');
  // The backend's backstop: the check said open, but the tenant is easy and still blocked
  // (409 easy_go_live_required). The pick is kept; the easy state is asked again.
  const [goLiveRequired, setGoLiveRequired] = useState(false);

  const setDefault = useMutation({
    mutationFn: (policyId: number) => tourOperatorsApi.update(topId, { default_launch_policy: policyId }),
    onMutate: () => setGoLiveRequired(false),
    onSuccess: () => {
      snackbar.show('Tenant default launch policy updated.', 'success');
      setPick('');
      void qc.invalidateQueries({ queryKey: ['preflight', 'onboarding-profile', topId] });
      void qc.invalidateQueries({ queryKey: ['tourOperators', 'detail', topId] });
    },
    onError: (e) => {
      if (easyGoLiveRequired(e)) {
        setGoLiveRequired(true);
        void qc.invalidateQueries({ queryKey: easyApi.easyStateKey(topId) });
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not update the default policy.', 'error');
    },
  });

  // The three switches disagree — say so instead of letting the board look fine.
  const sendsOn =
    state.restech.send_invitations_webhook === true ||
    state.restech.send_surveys === true ||
    state.notifications.enabled > 0;
  const guestOpen = Boolean(
    def &&
      (def.flags.outgoing_emails || def.flags.outgoing_sms || def.flags.outgoing_whatsapp || def.flags.outgoing_airbnb),
  );
  const disagreements: string[] = [];
  if (state.enable_launch_policies && blocked && sendsOn) {
    disagreements.push(
      'Sends are switched on but the default policy still blocks every channel — nothing goes out and no booking comes in until tours move off it.',
    );
  }
  if (!blocked && state.restech.provider && state.restech.send_invitations_webhook === false) {
    disagreements.push(
      'The policy admits bookings but the booking system’s invitation webhooks are off (Enable soft-launch notifications).',
    );
  }
  if (guestOpen && state.notifications.canned_total > 0 && state.notifications.enabled === 0) {
    disagreements.push('The default policy opens the guest channels but no standard notification is enabled.');
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="launch-state-card">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <RocketLaunchIcon fontSize="small" color={blocked ? 'warning' : 'success'} />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Launch state
        </Typography>
        {!state.enable_launch_policies ? (
          <Chip size="small" variant="outlined" label="Launch policies disabled for this tenant" />
        ) : def ? (
          <Chip
            size="small"
            color={blocked ? 'warning' : 'success'}
            label={`${def.name} — incoming bookings ${blocked ? 'blocked' : 'admitted'}`}
          />
        ) : (
          <Chip size="small" variant="outlined" label="No default policy — everything admitted" />
        )}
      </Box>

      {!state.enable_launch_policies && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <code>enable_launch_policies</code> is off in this tenant’s config, so every policy flag
          check passes: bookings and sends are not gated by the policy at all.
        </Alert>
      )}

      {state.enable_launch_policies && blocked && def && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <b>{defaultRow?.effective_tours_count ?? 0}</b> of {state.tours_total} tour(s) are on{' '}
          <b>{def.name}</b>, which drops incoming bookings ({state.incoming_bookings_block_reason}).
          The Booking backfill step cannot start, and a booking webhook FareHarbor registers is
          discarded, until tours move to a policy that admits bookings.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The launch policy is the manual go-live switch, taken once everything else works:{' '}
        <b>Draft</b> (nothing in, nothing out) → <b>Soft launched</b> (bookings in, chat on, messages
        only to explicit test addresses/numbers) → <b>Hard launched</b> (guest channels open). Move
        tours with the Tours grid’s <i>Set launch policy</i> batch operation.
      </Typography>

      {state.policies.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          {state.policies.map((p) => (
            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                variant={p.is_default ? 'filled' : 'outlined'}
                label={`${p.effective_tours_count} tour(s) on ${p.name}${p.is_default ? ' (tenant default)' : ''}`}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                {policyAdmits(p.flags)}
              </Typography>
              {canMoveTours && p.effective_tours_count > 0 && (
                <Button
                  size="small"
                  component={RouterLink}
                  to={toursOnPolicyLink(topId, p.id)}
                  aria-label={`Open the ${p.effective_tours_count} tour(s) on ${p.name} in the Tours grid`}
                >
                  Move these tours
                </Button>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {def && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Tooltip title="The batch operation sets the policy per tour; the tenant default is what tours created later (e.g. by a catalogue re-sync) inherit. Change it here so a re-sync does not silently reintroduce Draft tours.">
            <Typography variant="caption" color="text.secondary">
              {state.tours_inheriting_default} tour(s) inherit the tenant default ({def.name}); tours a
              later catalogue sync creates will too.
            </Typography>
          </Tooltip>
          {canSetDefault && easy.open && state.policies.length > 1 && (
            <>
              <LaunchPolicyPicker
                policies={state.policies.filter((p) => !p.is_default)}
                value={pick}
                onChange={setPick}
                label="Tenant default"
                ariaLabel="Tenant default launch policy"
                sx={{ minWidth: 200 }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={pick === '' || setDefault.isPending}
                onClick={() => pick !== '' && setDefault.mutate(pick)}
              >
                Set as tenant default
              </Button>
            </>
          )}
        </Box>
      )}

      {easy.blocked && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={
            <Button
              size="small"
              color="inherit"
              component={RouterLink}
              to={easyOnboardHref(topId)}
            >
              Go live in easy setup
            </Button>
          }
        >
          This tenant was set up with easy onboarding, and its automatic guest messages are
          blocked until it goes live. Go live from its easy setup: that lifts the block and sets
          the tenant default together. Changing the default or moving tours to another policy here
          would leave the messages blocked.
        </Alert>
      )}
      {goLiveRequired && !easy.blocked && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          data-testid="easy-go-live-required"
          action={
            <Button size="small" color="inherit" component={RouterLink} to={easyOnboardHref(topId)}>
              Go live in easy setup
            </Button>
          }
        >
          The tenant default was not changed. {easyGoLiveRequiredWords([topId])}
        </Alert>
      )}
      {easy.unknown && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button size="small" color="inherit" onClick={easy.retry}>
              Try again
            </Button>
          }
        >
          Could not check whether this tenant goes live through easy setup, so neither the tenant
          default nor the tours’ policy can be changed from here right now.
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* The three switches, side by side. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
            Launch policy
          </Typography>
          <Typography variant="body2">
            {def ? def.name : 'none'} — {def ? policyAdmits(def.flags) : 'everything admitted'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
            Booking-system sends
          </Typography>
          <Typography variant="body2">
            {state.restech.provider
              ? `${state.restech.provider}: invitation webhooks ${onOff(state.restech.send_invitations_webhook)}, surveys ${onOff(state.restech.send_surveys)}`
              : 'no booking system in the profile'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="overline" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
            Standard notifications
          </Typography>
          <Typography variant="body2">
            {state.notifications.canned_total > 0
              ? `${state.notifications.enabled} of ${state.notifications.canned_total} enabled`
              : 'none materialised yet'}
          </Typography>
        </Box>
      </Box>

      {disagreements.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Stack spacing={0.5}>
            {disagreements.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </Stack>
        </Alert>
      )}
    </Paper>
  );
}
